import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Check, ChevronDown, ChevronUp, Loader2, MapPin } from 'lucide-react';
import { API_BASE, GROUP_PRIORITY_OPTIONS, AVAILABILITY_META } from './constants';
import { AvailabilityDots, OfferDetail } from './shared';
import DateInput from '@/components/ui/date-input';
import { vokalSpelling } from '@/lib/utils';
import { formatCourseOfferLine, formatOfferSchedule } from './courseOfferLabel';

function GroupPanel({ form, setRoot }) {
  const g = form.group;
  const setGroup = (patch) => setRoot('group', { ...g, ...patch });
  const setStudent = (idx, key, val) => setGroup({
    additional_students: g.additional_students.map((s, i) => (i === idx ? { ...s, [key]: val } : s)),
  });
  return (
    <div className="border border-brand-red/20 bg-brand-red/5 rounded-xl p-4 space-y-3" data-testid="public-registration-group-panel">
      <div className="text-sm text-slate-700 space-y-2">
        <p>Sie haben die Möglichkeit, weitere Schüler anzumelden, damit der Gruppenunterricht zeitnah beginnen kann.</p>
        <p>Wenn Sie keine weiteren Schüler anmelden wollen, kommen Sie auf die Warteliste, bis ein passender Schüler gefunden ist.</p>
        <p>Alternativ können wir einen 20-minütigen Einzelunterricht anbieten, der preislich identisch mit dem Gruppenunterricht ist. Möchten Sie die Alternative für den 20-minütigen Einzelunterricht wahrnehmen?</p>
      </div>
      <div>
        <Label>Bitte wählen Sie Ihre Priorität! *</Label>
        <Select
          value={g.priority}
          onValueChange={(v) => setGroup({
            priority: v,
            additional_students: v === 'add_students' && g.additional_students.length === 0
              ? [{ first_name: '', last_name: '', birthdate: '' }]
              : g.additional_students,
          })}
        >
          <SelectTrigger className="bg-white" data-testid="public-registration-group-priority">
            <SelectValue placeholder="Bitte wählen" />
          </SelectTrigger>
          <SelectContent>
            {GROUP_PRIORITY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`public-registration-group-priority-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {g.priority === 'add_students' && (
        <div className="space-y-3" data-testid="public-registration-group-students">
          <div className="text-sm font-medium text-slate-800">Weitere Schüler für den Gruppenunterricht</div>
          {g.additional_students.map((s, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end bg-white border border-slate-200 rounded-lg p-3">
              <div>
                <Label className="text-xs">Vorname *</Label>
                <Input value={s.first_name} onChange={(e) => setStudent(idx, 'first_name', e.target.value)} data-testid={`public-registration-group-student-${idx}-firstname`} />
              </div>
              <div>
                <Label className="text-xs">Nachname *</Label>
                <Input value={s.last_name} onChange={(e) => setStudent(idx, 'last_name', e.target.value)} data-testid={`public-registration-group-student-${idx}-lastname`} />
              </div>
              <div>
                <Label className="text-xs">Geburtsdatum</Label>
                <DateInput  value={s.birthdate || ''} onChange={(e) => setStudent(idx, 'birthdate', e.target.value)} data-testid={`public-registration-group-student-${idx}-birthdate`} />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-rose-500 hover:text-rose-700"
                onClick={() => setGroup({ additional_students: g.additional_students.filter((_, i) => i !== idx) })}
                disabled={g.additional_students.length <= 1}
                data-testid={`public-registration-group-student-${idx}-remove`}
              >
                ✕
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setGroup({ additional_students: [...g.additional_students, { first_name: '', last_name: '', birthdate: '' }] })}
            data-testid="public-registration-group-add-student-btn"
          >
            + Weiteren Schüler hinzufügen
          </Button>
        </div>
      )}

      {g.priority === 'waitlist' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="public-registration-group-waitlist-info">
          Sie werden benachrichtigt, sobald wir einen weiteren passenden Schüler für den Gruppenunterricht gefunden haben.
        </div>
      )}

      {g.priority === 'switch_20min' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="public-registration-group-20min-info">
          Prima! Im nächsten Schritt suchen wir freie Termine für Ihren 20-minütigen Einzelunterricht.
        </div>
      )}
    </div>
  );
}

const formatDeadline = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y}` : '';
};

/** Termin plus Anzahl der Einzeltermine, sofern gepflegt. */
const termin = (o) => {
  const count = o.lesson_count > 1 ? `${o.lesson_count} Termine` : '';
  return [formatOfferSchedule(o.schedule_text), count].filter(Boolean).join('\n');
};

export function CourseOffersPanel({ tenantSlug, subject, selected, onSelect }) {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!subject) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_BASE}/public/registrations/${encodeURIComponent(tenantSlug)}/course-offers`,
          { params: { subject } },
        );
        if (!cancelled) {
          setOffers(r.data.offers || []);
          setExpanded((r.data.offers || []).length === 1 ? r.data.offers[0].id : null);
        }
      } catch (_e) {
        if (!cancelled) setOffers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subject, tenantSlug]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="course-offers-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Kursangebote werden gesucht …
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="course-offers-panel">
      {offers.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600" data-testid="course-offers-none">
          Aktuell ist online kein passendes Kursangebot verfügbar — wir melden uns nach Ihrer Anmeldung persönlich bei Ihnen.
        </div>
      ) : (
        <>
          <div className="text-sm text-slate-700" data-testid="course-offers-count">
            {offers.length === 1 ? 'Es wurde ein Kursangebot gefunden.' : `Es wurden ${offers.length} Kursangebote gefunden.`}
          </div>
          <Button type="button" onClick={() => setOpen(true)} data-testid="course-offers-show-btn">
            Kursangebote anzeigen
          </Button>
        </>
      )}

      {selected && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="course-offer-selected">
          <Check className="h-4 w-4 shrink-0" />
          <span>Gewähltes Kursangebot: <strong>{formatCourseOfferLine(selected)}</strong></span>
          <button type="button" className="ml-auto text-emerald-700 underline text-xs" onClick={() => onSelect(null)} data-testid="course-offer-unselect">
            entfernen
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="course-offers-dialog">
          <DialogHeader>
            <DialogTitle>Kursangebote</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {offers.map((o) => {
              const isOpen = expanded === o.id;
              return (
                <div key={o.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`course-offer-${o.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : o.id)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 ${isOpen ? 'bg-brand-red/5' : 'bg-white hover:bg-slate-50'}`}
                    data-testid={`course-offer-${o.id}-toggle`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold ${isOpen ? 'text-brand-red' : 'text-slate-900'}`}>{formatCourseOfferLine(o)}</div>
                      {o.location_text && <div className="text-xs text-slate-500 mt-0.5">{o.location_text}</div>}
                    </div>
                    {o.availability && <AvailabilityDots availability={o.availability} />}
                    {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400 mt-1" /> : <ChevronDown className="h-4 w-4 text-slate-400 mt-1" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 py-3 border-t border-slate-100 space-y-2 text-sm">
                      <OfferDetail label="Auslastung" value={o.availability ? (AVAILABILITY_META[o.availability] || AVAILABILITY_META.free).label : ''} />
                      <OfferDetail label="Termin" value={[termin(o), o.location_text].filter(Boolean).join('\n')} />
                      <OfferDetail label="Dozent:in" value={o.teacher_name} />
                      <OfferDetail label="Beschreibung" value={o.description} />
                      <OfferDetail label="Zeitraum" value={o.period_text} />
                      <OfferDetail label="Vorkenntnisse" value={o.prior_knowledge} />
                      <OfferDetail label="Zielgruppe" value={o.target_group} />
                      <OfferDetail label="Voraussetzung" value={o.requirements} />
                      <OfferDetail label="Material" value={o.material} />
                      <OfferDetail label="Kosten" value={o.cost_text} />
                      <OfferDetail label="Bemerkungen" value={o.remarks} />
                      <OfferDetail label="Ansprechpartner" value={o.contact_person} />
                      <OfferDetail label="Anmeldeschluss" value={formatDeadline(o.registration_deadline)} />
                      <Button
                        type="button"
                        size="sm"
                        className="mt-1"
                        onClick={() => {
                          onSelect({
                            id: o.id,
                            source: o.source,
                            title: formatCourseOfferLine(o),
                            cost_text: o.cost_text || '',
                            location_id: o.location_id || '',
                            location_text: o.location_text || '',
                          });
                          setOpen(false);
                        }}
                        data-testid={`course-offer-${o.id}-select`}
                      >
                        Auswählen
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="course-offers-close">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function StepSubject({ form, setField, setRoot, categories, categoryVisible, courseSubject, subjectOptions, locations, tenantSlug, F, title, invalid }) {
  const s = form.student;
  const ec = (k) => (invalid(k) ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : '');
  const [subjectLocations, setSubjectLocations] = useState(null);

  useEffect(() => {
    if (!s.instrument) { setSubjectLocations(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API_BASE}/public/registrations/${encodeURIComponent(tenantSlug)}/locations`,
          { params: { subject: s.instrument } },
        );
        if (!cancelled) setSubjectLocations(r.data.locations || []);
      } catch (_e) {
        if (!cancelled) setSubjectLocations(null);
      }
    })();
    return () => { cancelled = true; };
  }, [s.instrument, tenantSlug]);

  const effectiveLocations = (subjectLocations && subjectLocations.length > 0) ? subjectLocations : locations;

  useEffect(() => {
    if (form.course_offer) return;
    if (form.location_id && !effectiveLocations.some((l) => l.id === form.location_id)) {
      setRoot('location_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocations, form.course_offer]);
  const allItems = categories.flatMap((c) => (c.items || []).map((it) => ({ cat: c.name, item: it })));
  const selectSubject = (value) => {
    const found = allItems.find((x) => x.item === value);
    setField('student', 'category', found?.cat || '');
    setField('student', 'instrument', value);
    setField('student', 'lesson_type', '');
    setRoot('teaching_type', '');
    setRoot('course_offer', null);
    if (form.course_offer) setRoot('location_id', '');
    setRoot('group', { priority: '', additional_students: [] });
  };
  const selectTeachingType = (v) => {
    setRoot('teaching_type', v);
    const isGroup = v.toLowerCase().includes('gruppen');
    setField('student', 'lesson_type', isGroup ? 'Gruppenunterricht' : 'Einzelunterricht');
    if (!isGroup) setRoot('group', { priority: '', additional_students: [] });
  };
  const notesSpec = F('student.notes');

  return (
    <div className="space-y-6" data-testid="public-registration-step-subject">
      <div>
        <h2 className="text-lg md:text-lg font-semibold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500 mt-1">Welches Fach möchten Sie belegen?</p>
      </div>

      {categories.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="public-registration-no-subjects">
          Aktuell sind keine Fächer hinterlegt. Bitte kontaktieren Sie uns direkt.
        </div>
      ) : (
        <div>
          <Label className={invalid('subject.instrument') ? 'text-red-600' : ''}>Fach *</Label>
          <Select value={s.instrument} onValueChange={selectSubject}>
            <SelectTrigger className={`mt-1 ${ec('subject.instrument')}`} data-testid="public-registration-subject-select">
              <SelectValue placeholder="Bitte wählen" />
            </SelectTrigger>
            <SelectContent>
              {categoryVisible ? (
                categories.map((c, i) => (
                  <SelectGroup key={c.name}>
                    <SelectLabel className="text-slate-400 font-normal pt-2 pb-0.5" data-testid={`public-registration-subject-category-${c.name}`}>
                      {i + 1}. {c.name}
                    </SelectLabel>
                    {(c.items || []).map((it) => (
                      <SelectItem key={it} value={it} className="text-slate-900 font-medium pl-8 py-1" data-testid={`public-registration-subject-${it}`}>
                        {it}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))
              ) : (
                allItems.map((x) => (
                  <SelectItem key={`${x.cat}-${x.item}`} value={x.item} className="text-slate-900 font-medium" data-testid={`public-registration-subject-${x.item}`}>
                    {x.item}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {s.instrument && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="public-registration-subject-selected">
          <Check className="h-4 w-4 shrink-0" />
          <span>Ausgewählt: <strong>{s.instrument}</strong>{s.category ? ` (${vokalSpelling(s.category)})` : ''}</span>
        </div>
      )}

      {/* Typ A: Unterrichtsart */}
      {s.instrument && !courseSubject && subjectOptions.length > 0 && (
        <div data-testid="public-registration-teaching-type-block">
          <Label className={invalid('subject.teaching_type') ? 'text-red-600' : ''}>Unterrichtsart *</Label>
          <Select value={form.teaching_type} onValueChange={selectTeachingType}>
            <SelectTrigger className={`mt-1 ${ec('subject.teaching_type')}`} data-testid="public-registration-teaching-type-select">
              <SelectValue placeholder="Bitte wählen" />
            </SelectTrigger>
            <SelectContent>
              {subjectOptions.map((o) => (
                <SelectItem key={o.name} value={o.name} data-testid={`public-registration-teaching-type-${o.name}`}>
                  <span>{o.name}</span>
                  {o.price_text && <span className="text-slate-400 ml-2">— {o.price_text}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {s.lesson_type === 'Gruppenunterricht' && <GroupPanel form={form} setRoot={setRoot} />}

      {/* Typ B: Kursangebote */}
      {s.instrument && courseSubject && (
        <CourseOffersPanel
          tenantSlug={tenantSlug}
          subject={s.instrument}
          selected={form.course_offer}
          onSelect={(o) => {
            setRoot('course_offer', o);
            setRoot('location_id', o && o.location_text ? (o.location_id || '') : '');
          }}
        />
      )}

      {/* Unterrichtsort: Wunsch nur ohne konkretes Kursangebot */}
      {s.instrument && !form.course_offer && effectiveLocations.length > 0 && (
        <div data-testid="public-registration-location-block">
          <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> Gewünschter Unterrichtsort (optional)</Label>
          <p className="text-xs text-slate-500 mt-0.5 mb-1">Standort der Schule — unabhängig vom Wohnort, den Sie zur PLZ angeben.</p>
          <Select value={form.location_id} onValueChange={(v) => setRoot('location_id', v)}>
            <SelectTrigger className="mt-1" data-testid="public-registration-location-select">
              <SelectValue placeholder="Bitte wählen" />
            </SelectTrigger>
            <SelectContent>
              {effectiveLocations.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`public-registration-location-${l.id}`}>
                  {l.name}{l.address ? ` — ${l.address}${l.postal_code || l.city ? `, ${[l.postal_code, l.city].filter(Boolean).join(' ')}` : ''}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {form.course_offer?.location_text && (
        <p className="text-sm text-slate-600" data-testid="public-registration-location-from-offer">
          Unterrichtsort: {form.course_offer.location_text}
        </p>
      )}

      {/* Anmerkungen */}
      {s.instrument && notesSpec.visible && (
        <div data-testid="public-registration-notes-block">
          {categories.some((c) => (c.items || []).some((i) => /jeki|emc|musical/i.test(i))) && (
            <p className="text-sm text-slate-600 mb-1.5">
              Bei Teilnahme an Schulkursen teilen Sie uns bitte im nächsten Feld die Klasse mit
              und ob Ihr Kind in eine Betreuung (OGS etc.) geht.
            </p>
          )}
          <Textarea
            rows={3}
            maxLength={300}
            value={s.notes}
            onChange={(e) => setField('student', 'notes', e.target.value.slice(0, 300))}
            placeholder="Anmerkungen …"
            data-testid="public-registration-student-notes"
          />
          <div className="text-xs text-slate-400 text-right mt-1">{(s.notes || '').length} von 300 Zeichen</div>
        </div>
      )}
    </div>
  );
}
